import * as React from 'react';

import { DeleteOutlined } from '@ant-design/icons';
import { Form, Switch } from 'antd';
import {
  ButtonType,
  Column,
  Modal,
  Notification,
  Row,
  Space,
  Text,
} from 'farmhub-ui-core';

import { useAppSelector, useAppDispatch } from 'app';
import {
  CButton,
  dataEmpty,
  Empty,
  IconActionTable,
  NameText,
  SearchTable,
  Table,
  TitleText,
} from 'components';
import { useFetchData } from 'hooks';
import {
  createOption,
  deleteOption,
  findOption,
  updateOption,
  updateMultiProductOptionsBuyProductID,
  updateMultiOrderOptionsBuyOrderID,
  updateMultiEntityOptionsBuyEntityID,
  updateOptionOfEntity,
  updateOptionStatus,
} from 'slice';
import { theme } from 'styled';
import {
  OPTION_TYPE,
  OPTION_TYPE_STRING,
  getDummyArray,
  hasLoadLastPagePagination,
  renderTableSkeleton,
  replaceItemInList,
  replaceItemInListOptimize,
} from 'utils';

import SelectColor from './components/SelectColor';
import UpdateOptionInput from './components/UpdateOptionInput';
import { CInput, TableLoadingStyle, TableStyle, Wrapper } from './styles';

const ACTION_TYPE = {
  DELETE: 'DELETE',
};

const initQuery = {
  relations: ['color'],
  order: { createdAt: 1 },
  page: 1,
  take: 10,
  reserved: true,
};

interface Icolor {
  id: string;
  name: string;
  code: string;
}

interface CreateTagModalProps {
  isVisible: boolean;
  id?: string;
  name?: string;
  type: 'PRODUCT' | 'ORDER' | 'ENTITY';
  initOptions: any[];
  setIsVisible: (e: boolean) => void;
  onAfterSubmit: (
    e: any,
    deletedOptionIDs?: string[],
    typeOptions?: any[]
  ) => void;
}

const CreateTagModal = ({
  isVisible,
  id,
  name,
  type,
  initOptions,
  setIsVisible,
  onAfterSubmit,
}: CreateTagModalProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const userID = useAppSelector(state => state.auth.item?.id);
  const entityID = useAppSelector(state => state.account.item?.id);
  const colors = useAppSelector(state => state.color.list);
  const [form] = Form.useForm();
  const [isLoadingOk, setIsLoadingOk] = React.useState<boolean>(false);
  const [color, setColor] = React.useState<Icolor | undefined>(undefined);
  const [queryOptions, setQueryOptions] = React.useState<any>(initQuery);
  const [loadingActionRecordIDs, setLoadingActionRecordIDs] = React.useState<
    string[]
  >([]);
  const [isLoadingCreate, setIsLoadingCreate] = React.useState(false);
  const [currentOptionID, setCurrentOptionID] = React.useState('');
  const [totalCreated, setTotalCreated] = React.useState(0);
  const [selectedOptions, setSelectedOptions] =
    React.useState<any[]>(initOptions);

  const isOptionsChanged = React.useMemo(() => {
    const initOptionIDs = (initOptions || []).map(item => item?.id);
    const selectedOptionIDs = (selectedOptions || []).map(item => item?.id);

    return (
      initOptionIDs.length !== selectedOptionIDs.length ||
      !selectedOptionIDs.every(id => initOptionIDs.includes(id))
    );
  }, [initOptions, selectedOptions]);

  const inputRef = React.useRef<any>(null);

  const {
    data: options,
    setData: setOptions,
    onReFreshList,
    pagination,
    isLoading,
  } = useFetchData({
    formatData: item => {
      return {
        ...item,
        key: item.id,
      };
    },
    action: findOption,
    query: {
      ...queryOptions,
      where: {
        ownerID: type === 'ENTITY' ? userID : entityID,
        type: OPTION_TYPE[type],
      },
    },
    deps: [queryOptions, isVisible],
    isVisible,
  });

  const handleClear = (): void => {
    form.resetFields();
    setSelectedOptions([]);
    setTotalCreated(0);
    setCurrentOptionID('');
  };

  const handleResetCurrentOption = () => {
    setCurrentOptionID('');
  };

  const handleSelectColor = (color: any) => {
    setColor(color);
    inputRef.current.focus();
  };

  const handleOk = async (): Promise<void> => {
    setIsLoadingOk(true);
    if (type === OPTION_TYPE_STRING.ENTITY.en) {
      const optionIDs = selectedOptions
        .filter(item => item)
        ?.map(item => item.id);

      const body = {
        entityID: id || '',
        optionIDs,
      };

      try {
        const data = await dispatch(
          updateMultiEntityOptionsBuyEntityID(body)
        ).unwrap();
        const newOptions = (data || [])?.map(item => item.option);

        onAfterSubmit(newOptions, undefined, data || []);

        dispatch(
          updateOptionOfEntity({
            oldOptionID: initOptions[0]?.id,
            newOption: selectedOptions[0],
            entityID: id,
          })
        );

        setIsVisible(false);
      } catch (error: any) {
        Notification('error', 'Cập nhật nhãn sản phẩm thất bại', error);
      } finally {
        setIsLoadingOk(false);
      }
    }

    if (type === OPTION_TYPE_STRING.PRODUCT.en) {
      const optionIDs = selectedOptions
        .filter(item => item)
        ?.map(item => item.id);

      const body = {
        productID: id || '',
        optionIDs,
      };

      try {
        const data = await dispatch(
          updateMultiProductOptionsBuyProductID(body)
        ).unwrap();
        const newOptions = (data || [])?.map(item => item.option);

        onAfterSubmit(newOptions, undefined, data || []);

        setIsVisible(false);
      } catch (error: any) {
        Notification('error', 'Cập nhật nhãn sản phẩm thất bại', error);
      } finally {
        setIsLoadingOk(false);
      }
    }

    if (type === OPTION_TYPE_STRING.ORDER.en) {
      const optionIDs = selectedOptions
        .filter(item => item)
        ?.map(item => item.id);

      const body = {
        orderID: id || '',
        optionIDs,
      };

      try {
        const data = await dispatch(
          updateMultiOrderOptionsBuyOrderID(body)
        ).unwrap();
        const newOptions = (data || [])?.map(item => item.option);

        onAfterSubmit(newOptions, undefined, data || []);

        setIsVisible(false);
      } catch (error: any) {
        Notification('error', 'Cập nhật nhãn đơn hàng thất bại', error);
      } finally {
        setIsLoadingOk(false);
      }
    }
  };

  const handleCancel = (): void => {
    setIsVisible(false);
    handleClear();
  };

  const handlePaginationChange = e => {
    setTotalCreated(0);
    setQueryOptions({
      ...queryOptions,
      page: e.current,
      take: e.pageSize,
    });
  };

  const handleSelectChange = (ids, rows): void => {
    setSelectedOptions(rows);
  };

  const handleSearch = (value): void => {
    setQueryOptions(preQuery => {
      return {
        ...preQuery,
        'where.content.s': value,
        page: 1,
        take: 10,
      };
    });
  };

  const handleCreateOption = (e: any): void => {
    form.validateFields().then(async values => {
      const { content } = values;

      const body = {
        colorID: color?.id,
        content,
        ownerID: type === 'ENTITY' ? userID : entityID,
        type: OPTION_TYPE[type],
      };

      try {
        setIsLoadingCreate(true);
        const { data } = await dispatch(createOption(body)).unwrap();
        setOptions(prevState => [data].concat(prevState));
        setTotalCreated(prevState => prevState + 1);
        form.resetFields();
      } catch (error: any) {
        Notification('error', 'Tạo nhãn thất bại', error);
      } finally {
        setIsLoadingCreate(false);
      }
    });
  };

  const handleUpdateOption = async (body): Promise<void> => {
    try {
      const params = {
        id: currentOptionID,
        body,
      };

      const { data } = await dispatch(updateOption(params)).unwrap();

      if (data) {
        const { id: optionID, content, color: colorRes } = data;

        replaceItemInList(
          { id: optionID, content, color: colorRes },
          options,
          setOptions
        );

        const newList = replaceItemInList(
          { id: optionID, content, color: colorRes },
          selectedOptions,
          setSelectedOptions
        );

        onAfterSubmit(newList);
        setCurrentOptionID('');
      }
    } catch (error: any) {
      Notification('error', 'Cập nhật nhãn thất bại', error);
    }
  };

  const handleRemoveOption = async (id: string): Promise<void> => {
    setLoadingActionRecordIDs(prevState => [
      ...prevState,
      `${ACTION_TYPE.DELETE}_${id}`,
    ]);

    try {
      const { data } = await dispatch(deleteOption(id)).unwrap();
      const newOptionList = options.filter(item => item.id !== data.id);
      const newSelectedOptions = selectedOptions.filter(
        item => item.id !== data.id
      );

      setTotalCreated(prevState => prevState - 1);

      if (hasLoadLastPagePagination(newOptionList, pagination)) {
        setQueryOptions(prevState => {
          return { ...prevState, page: pagination?.current - 1 };
        });
      } else {
        if (newOptionList?.length == queryOptions?.take) {
          await onReFreshList();
        }

        setOptions(newOptionList);
        setSelectedOptions(newSelectedOptions);
        onAfterSubmit(newSelectedOptions, [id]);
      }
    } catch (error: any) {
      Notification('error', 'Xoá nhãn thất bại', error);
    } finally {
      setLoadingActionRecordIDs(prevState =>
        prevState.filter(item => item != `${ACTION_TYPE.DELETE}_${id}`)
      );
    }
  };

  const actionTableData = (id: string): any => [
    {
      action: () => {
        handleRemoveOption(id);
      },
      isLoading: loadingActionRecordIDs.includes(`${ACTION_TYPE.DELETE}_${id}`),
      icon: <DeleteOutlined className="icon" style={{ fontSize: 18 }} />,
      tooltip: 'Xóa',
    },
  ];

  const handleUpdateIsPublic = async (
    record: any,
    status: number
  ): Promise<void> => {
    const newStatus = status;
    const oldStatus = status == 0 ? 1 : 0;

    if (record?.id) {
      try {
        replaceItemInListOptimize(
          {
            id: record.id,
            status,
          },
          setOptions
        );
        await dispatch(
          updateOptionStatus({
            id: record.id,
            body: { status: newStatus },
          })
        ).unwrap();
      } catch (error: any) {
        Notification('error', 'Cập nhật công khai thất bại', error);
        replaceItemInListOptimize(
          {
            id: record.id,
            status: oldStatus,
          },
          setOptions
        );
      }
    }
  };

  const columns: Array<any> = [
    {
      width: 320,
      title: <NameText>Tên nhãn</NameText>,
      key: 'option',
      render: (option: any) => (
        <UpdateOptionInput
          handleResetCurrentOption={handleResetCurrentOption}
          isEditing={currentOptionID === option.id}
          option={option}
          onClick={() => setCurrentOptionID(option.id)}
          onSubmit={handleUpdateOption}
        />
      ),
    },
    {
      width: 75,
      ellipsis: true,
      align: 'center',
      title: <NameText>Công khai</NameText>,
      dataIndex: 'status',
      key: 'status',
      render: (status: any, record: any) => (
        <Switch
          checked={status == 0}
          size="small"
          onChange={() => handleUpdateIsPublic(record, status == 0 ? 1 : 0)}
        />
      ),
    },
    {
      width: 50,
      key: 'id',
      dataIndex: 'id',
      render: (optionID: string) => (
        <Column className="delete-icon" justifyContent="flex-start">
          <IconActionTable data={actionTableData(optionID)} />
        </Column>
      ),
    },
  ];

  React.useEffect(() => {
    const initData = () => {
      setColor(colors[0]);
      setSelectedOptions(initOptions);
    };
    if (isVisible) {
      initData();
    }
  }, [isVisible, initOptions, colors]);

  return (
    <Wrapper onClick={e => e.stopPropagation()}>
      <Modal
        centered
        destroyOnClose
        bodyHeight="600px"
        footer={[
          <CButton
            key="1"
            type={ButtonType.Text}
            width="auto"
            onClick={handleCancel}
          >
            Hủy
          </CButton>,
          <CButton
            key="2"
            disabled={!isOptionsChanged}
            loading={isLoadingOk}
            type={ButtonType.Primary}
            width="auto"
            onClick={handleOk}
          >
            Xác nhận
          </CButton>,
        ]}
        title={
          <TitleText>
            Thêm nhãn cho {OPTION_TYPE_STRING[type].vi}{' '}
            <Text color="main" weight="extraBold">
              {name}
            </Text>
          </TitleText>
        }
        visible={isVisible}
        width="600px"
        onCancel={handleCancel}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label={
              <Row justifyContent="space-between" width="535px">
                <Text weight="bold">Thêm nhãn</Text>{' '}
                <Text color="secondary" size="xxs">
                  (Nhấn Enter để tạo nhãn)
                </Text>
              </Row>
            }
            name="content"
            rules={[
              {
                required: true,
                message: 'Bạn chưa nhập tên nhãn!',
              },
            ]}
          >
            <CInput
              ref={inputRef}
              disabled={isLoadingCreate}
              height={theme.input.height.md}
              maxLength={50}
              placeholder="Nhập tên nhãn"
              suffix={
                <SelectColor color={color} setColor={handleSelectColor} />
              }
              onPressEnter={handleCreateOption}
            />
          </Form.Item>
        </Form>
        <Row justifyContent="space-between">
          <Column>
            <Text color="primary" lineHeight="22px" weight="bold">
              Danh sách nhãn:
            </Text>
            <Text color="secondary" size="xxs" styles={{ fontSize: '12px' }}>
              Đã chọn {selectedOptions?.length} nhãn
            </Text>
          </Column>
          <Column alignItems="flex-end">
            <SearchTable
              height="sm"
              placeholder="Tìm kiếm nhãn"
              width={220}
              onChange={e => handleSearch(e.target.value)}
            />
          </Column>
        </Row>

        <Table
          columns={isLoading ? renderTableSkeleton(tableSkeleton) : columns}
          dataSource={
            isLoading
              ? getDummyArray(10)
              : [...options.slice(0, queryOptions.take)]
          }
          locale={{
            emptyText: (
              <>
                <Space height={24} />
                <Empty
                  description="Chưa có nhãn"
                  img={dataEmpty.OBJECT}
                  minHeight={120}
                />
              </>
            ),
          }}
          pagination={{
            ...pagination,
            total: pagination.total + totalCreated,
          }}
          rowKey="id"
          rowSelection={{
            type: type === 'ENTITY' ? 'radio' : 'checkbox',
            selectedRowKeys: selectedOptions?.map(item => item?.id),
            onChange: handleSelectChange,
            preserveSelectedRowKeys: true,
          }}
          styles={isLoading ? TableLoadingStyle : TableStyle}
          onChange={handlePaginationChange}
        />
      </Modal>
    </Wrapper>
  );
};

export default CreateTagModal;

const tableSkeleton = [
  {
    width: 320,
    title: 'Tên nhãn',
    type: 'input',
  },
  {
    width: 50,
    type: 'input',
  },
];
